import React, { FC, useMemo } from 'react';

import * as Sentry from '@sentry/react';
import * as Yup from 'yup';

import styled from 'styled-components';
import { Field, FieldArray, Form, Formik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Flex,
  Spinner,
  Toggle,
  useModal,
  useToastContext,
} from '@resideo/blueprint-react';
import { InputField } from '@resideo/blueprint-formik';

import PageLayout from 'components/common/PageLayout';
import PageLoading from 'components/common/PageLoading';
import Typography from 'components/common/Typography';

import { useWebhook } from 'hooks/useDataStream';
import { useRisClient } from 'hooks/useRisClient';

import { notEmpty } from 'utils/common';
import { checkCallbackURL } from 'utils/validation';

import FAQModalContent from './FAQModalContent';

import {
  AddIcon,
  DataStreamRequirements,
  RemoveIcon,
} from '../DataStreamScreen.style';

import { RisError, UsersCreateWebhookRequest } from 'types/types';

const { Description } = PageLayout;
const { Text, Title } = Typography;

const HeadersEntry = styled(Box)`
  display: flex;
  gap: 35px;
  height: 97px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

let headerId = 0;

const DataStreamSetupScreen: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { client } = useRisClient();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const { data: webhook, isLoading } = useWebhook();

  const { Modal, openModal, closeModal } = useModal({
    newDesign: true,
  });

  const saveWebhook = useMutation({
    mutationKey: ['dataStream', 'saveWebhook'],
    mutationFn: async (requestBody: UsersCreateWebhookRequest) => {
      if (!webhook?.id) {
        return (await client.myUsers.myUsersCreateWebhook(requestBody)).data;
      } else {
        return (
          await client.myUsers.myUsersUpdateWebhook(webhook.id, requestBody)
        ).data;
      }
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['dataStream', 'webhooks'] });

      const previousWebhook = queryClient.getQueryData([
        'dataStream',
        'webhooks',
      ]);

      return { previousWebhook };
    },

    onSuccess: data => {
      addToast({
        toastType: 'Success',
        message: t('privacy.dataStream.setup.success.webhookSaved'),
      });

      queryClient.setQueryData(['dataStream', 'webhooks'], data);

      navigate('/privacy-and-data/data-stream');
    },

    onError: (error: RisError, variables, context) => {
      const fallbackMessage = t('privacy.dataStream.setup.errors.saveFailed');

      const { errors } = error.body;

      errors
        .map(err => err.message)
        .filter(Boolean)
        .forEach(message =>
          addToast({ toastType: 'Error', message: message || fallbackMessage })
        );

      queryClient.setQueryData(
        ['dataStream', 'webhooks'],
        context?.previousWebhook
      );

      Sentry.captureEvent({
        message: '[Data Streaming] Error saving webhook',
        level: 'error',
        extra: { variables },
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'webhooks'],
        refetchType: 'none',
      });
    },

    retry: false,
  });

  const CallbackURLSchema = Yup.object().shape({
    callbackUrl: Yup.string()
      .required(t('common.validation.required'))
      .max(2048, t('common.validation.urlMaxLength', { length: 2048 }))
      .test(
        'is-valid-url',
        t('common.validation.urlInvalid'),
        (value, context) =>
          !!value && checkCallbackURL(context.parent.callbackUrl)
      ),

    isEnabled: Yup.boolean().required(),

    headers: Yup.array()
      .of(
        Yup.object().shape(
          {
            header: Yup.string()
              .nullable()
              .when('value', {
                is: (value: string) => value,
                then: schema =>
                  schema.required(t('common.validation.required')),
              }),

            value: Yup.string()
              .nullable()
              .when('header', {
                is: (header: string) => header,
                then: schema =>
                  schema.required(t('common.validation.required')),
              }),

            id: Yup.string().optional(),
          },
          [['header', 'value']]
        )
      )
      .required(),
  });

  const initialValues = useMemo(() => {
    return {
      callbackUrl: webhook?.callbackUrl || '',
      isEnabled: webhook?.isEnabled !== undefined ? webhook.isEnabled : true,
      headers: webhook?.headers.length
        ? webhook.headers
        : [{ header: '', value: '', id: '' }],
    };
  }, [webhook]);

  const handleSubmit = (formData: Yup.InferType<typeof CallbackURLSchema>) => {
    const requestBody = {
      callbackUrl: formData.callbackUrl,
      headers:
        formData.headers
          ?.map(header => {
            if (header.header && header.value) {
              return header;
            }

            return null;
          })
          .filter(notEmpty) || [],
      isEnabled: formData.isEnabled,
    };

    saveWebhook.mutate(requestBody);
  };

  return (
    <PageLayout
      data-test='data-stream-setup-screen'
      title={t('privacy.dataStream.setup.title')}>
      <Description data-test='data-stream-setup-description'>
        <DataStreamRequirements />

        <Button
          data-test='data-stream-setup-faq'
          variant='tertiary'
          style={{ paddingLeft: 12 }}
          onClick={() => openModal()}>
          {t('privacy.dataStream.setup.faq')}
        </Button>
      </Description>

      {isLoading ? (
        <PageLoading />
      ) : (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={CallbackURLSchema}
          enableReinitialize={true}>
          {({ values, dirty, isValid }) => (
            <Form data-test='data-stream-setup-form'>
              <Flex
                alignItems='center'
                justifyContent='space-between'
                paddingTop='25px'>
                <Flex flexDirection='column'>
                  <Title level={2}>
                    {t('privacy.dataStream.setup.urlDetails')}
                  </Title>

                  <Text>
                    {t('privacy.dataStream.setup.urlDetailsDescription')}
                  </Text>
                </Flex>

                <Button
                  data-test='data-stream-setup-submit-form'
                  type='submit'
                  disabled={!dirty || !isValid || saveWebhook.isPending}
                  size='small'>
                  {saveWebhook.isPending ? (
                    <Spinner
                      aria-label={t('common.connecting')}
                      color='white'
                    />
                  ) : (
                    t('privacy.dataStream.setup.saveWebhook')
                  )}
                </Button>
              </Flex>

              <p>{t('common.requiredFields')}</p>

              <Box marginBottom='medium' width={1 / 3}>
                <InputField
                  data-test='data-stream-setup-url-input'
                  type='text'
                  version='v2'
                  required
                  label={t('privacy.dataStream.setup.callbackURL')}
                  name='callbackUrl'
                />
              </Box>

              <ToggleContainer>
                <Field name='isEnabled'>
                  {({ form, field }) => (
                    <Toggle
                      data-test='data-stream-setup-toggle'
                      name={field.name}
                      checked={field.value}
                      onChange={e => {
                        form.setFieldValue(field.name, e.target.checked);
                      }}
                    />
                  )}
                </Field>
                Enabled
              </ToggleContainer>

              <Flex flexDirection='column' paddingTop='40px'>
                <Title level={2}>
                  {t('privacy.dataStream.setup.headers.title')}
                </Title>

                <FieldArray name='headers'>
                  {({ push, remove }) => (
                    <Box>
                      {values.headers &&
                        values.headers.map((header, index) => (
                          <HeadersEntry
                            data-test={`data-stream-setup-header-${index}`}
                            key={header.id}
                            width={3 / 4}>
                            <Box flexGrow='1'>
                              <InputField
                                data-test='data-stream-setup-header-key'
                                version='v2'
                                name={`headers.${index}.header`}
                                label={t(
                                  'privacy.dataStream.setup.headers.name'
                                )}
                              />
                            </Box>

                            <Box flexGrow='1'>
                              <InputField
                                data-test='data-stream-setup-header-value'
                                version='v2'
                                name={`headers.${index}.value`}
                                label={t(
                                  'privacy.dataStream.setup.headers.value'
                                )}
                              />
                            </Box>

                            <Button
                              data-test='data-stream-setup-remove-header'
                              type='button'
                              disabled={values.headers.length <= 1}
                              size='small'
                              variant='destructiveTertiary'
                              style={{ minWidth: 0, padding: 0 }}
                              onClick={() => remove(index)}>
                              <RemoveIcon style={{ paddingTop: '8px' }} />
                            </Button>
                          </HeadersEntry>
                        ))}

                      <Button
                        data-test='data-stream-setup-add-header'
                        type='button'
                        disabled={values.headers.length >= 5}
                        variant='tertiary'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '0',
                        }}
                        onClick={() =>
                          push({
                            header: '',
                            value: '',
                            id: `header-${++headerId}`,
                          })
                        }>
                        <AddIcon /> {t('privacy.dataStream.setup.headers.add')}
                      </Button>
                    </Box>
                  )}
                </FieldArray>
              </Flex>
            </Form>
          )}
        </Formik>
      )}

      <Modal data-test='data-stream-setup-faq-modal'>
        <FAQModalContent closeModal={closeModal} />
      </Modal>
    </PageLayout>
  );
};

export default DataStreamSetupScreen;
